import React, { useState, useEffect } from 'react';
import { getFullImageUrl, handleImageError } from '../utils/imageUtils';

interface TeamMember {
  _id: string;
  name: string;
  position: string;
  description: string;
  imageUrl: string;
  socialLinks: {
    twitter?: string;
    linkedin?: string;
    github?: string;
  };
  featured: boolean;
}

interface FormData {
  name: string;
  position: string;
  description: string;
  imageUrl: string;
  socialLinks: {
    twitter: string;
    linkedin: string;
    github: string;
  };
  featured: boolean;
}

const TeamSection: React.FC = () => {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    position: '',
    description: '',
    imageUrl: '',
    socialLinks: {
      twitter: '',
      linkedin: '',
      github: ''
    },
    featured: false
  });

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
      const response = await fetch(`${apiUrl}/api/team`);
      if (response.ok) {
        const data = await response.json();
        setMembers(data);
      }
    } catch (error) {
      console.error('Error fetching team members:', error);
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB');
      return;
    }

    setUploading(true);
    
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('logo', file);

      const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
      const response = await fetch(`${apiUrl}/api/upload/logo`, {
        method: 'POST',
        credentials: 'include',
        body: uploadFormData,
      });

      const result = await response.json();

      if (result.url) {
        setFormData(prev => ({
          ...prev,
          imageUrl: result.url
        }));
        
        console.log('Image uploaded to:', result.url);
        alert('Image uploaded successfully!');
      } else {
        alert(`Upload failed: ${result.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Error uploading image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name.trim()) {
      alert('Name is required');
      return;
    }
    if (!formData.position.trim()) {
      alert('Position is required');
      return;
    }
    
    try {
      const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
      const url = editingMember ? `${apiUrl}/api/team/${editingMember._id}` : `${apiUrl}/api/team`;
      const method = editingMember ? 'PUT' : 'POST';
      
      console.log('Saving team member:', { url, method, formData });
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(formData),
      });

      console.log('Response status:', response.status);

      if (response.ok) {
        fetchMembers();
        resetForm();
        alert(editingMember ? 'Team member updated!' : 'Team member added!');
      } else {
        try {
          const errorData = await response.json();
          alert(`Error saving team member: ${errorData.message || JSON.stringify(errorData)}`);
        } catch {
          alert(`Error saving team member: ${response.statusText}`);
        }
      }
    } catch (error) {
      console.error('Error saving team member:', error);
      alert('Error saving team member: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  const handleEdit = (member: TeamMember) => {
    setEditingMember(member);
    setFormData({
      name: member.name,
      position: member.position,
      description: member.description,
      imageUrl: member.imageUrl,
      socialLinks: {
        twitter: member.socialLinks.twitter || '',
        linkedin: member.socialLinks.linkedin || '',
        github: member.socialLinks.github || ''
      },
      featured: member.featured
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Are you sure you want to delete this team member?')) return;
    
    try {
      const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;
      const response = await fetch(`${apiUrl}/api/team/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        fetchMembers();
        alert('Team member deleted!');
      } else {
        alert('Error deleting team member');
      }
    } catch (error) {
      console.error('Error deleting team member:', error);
      alert('Error deleting team member');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      position: '',
      description: '',
      imageUrl: '',
      socialLinks: {
        twitter: '',
        linkedin: '',
        github: ''
      },
      featured: false
    });
    setEditingMember(null);
    setShowForm(false);
  };

  return (
    <div className="text-gray-100 font-sans h-full" style={{backgroundColor: '#0a0908', padding: '2rem'}}>
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-dusk-amber to-dusk-copper bg-clip-text text-transparent mb-2">Team Management</h1>
            <p className="text-gray-400">Manage DUSKPROOF team members</p>
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            className="px-6 py-3 bg-gradient-to-r from-dusk-copper to-dusk-amber hover:from-dusk-copper-deep hover:to-dusk-copper text-white font-semibold rounded-xl transition-all shadow-lg hover:shadow-dusk-copper/40"
          >
            {showForm ? 'Cancel' : 'Add Team Member'}
          </button>
        </div>

        {showForm && (
          <form onSubmit={handleSubmit} className="p-8 space-y-6 mb-8 rounded-2xl border border-[var(--dusk-line)] bg-[rgba(243,235,226,0.04)] backdrop-blur-sm">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Name
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded-md text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Position
                </label>
                <input
                  type="text"
                  required
                  value={formData.position}
                  onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                  className="w-full px-3 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded-md text-white"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-300">
                  Photo
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => document.getElementById('image-upload')?.click()}
                    className="px-3 py-1 bg-dusk-copper hover:bg-dusk-copper-deep text-white text-xs rounded"
                    disabled={uploading}
                  >
                    {uploading ? 'Uploading...' : 'Browse'}
                  </button>
                  {formData.imageUrl && (
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, imageUrl: '' })}
                      className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={formData.imageUrl}
                  onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                  className="flex-1 px-4 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-dusk-copper"
                  placeholder="Upload an image or enter URL manually"
                  readOnly={formData.imageUrl?.startsWith('/uploads/') || false}
                />
                <input
                  id="image-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </div>
              {formData.imageUrl && (
                <div className="mt-2 p-2 bg-[rgba(243,235,226,0.04)] rounded">
                  <p className="text-xs text-gray-400 mb-2">Photo Preview:</p>
                  <img 
                    src={getFullImageUrl(formData.imageUrl)} 
                    alt="" 
                    className="w-16 h-16 object-cover rounded-full border"
                    onError={handleImageError}
                  />
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded-md text-white h-24 resize-none"
                placeholder="Enter member description..."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Twitter URL
                </label>
                <input
                  type="text"
                  value={formData.socialLinks.twitter}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    socialLinks: { ...formData.socialLinks, twitter: e.target.value }
                  })}
                  className="w-full px-3 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded-md text-white"
                  placeholder="https://twitter.com/username"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  LinkedIn URL
                </label>
                <input
                  type="text"
                  value={formData.socialLinks.linkedin}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    socialLinks: { ...formData.socialLinks, linkedin: e.target.value }
                  })}
                  className="w-full px-3 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded-md text-white"
                  placeholder="https://linkedin.com/in/username"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  GitHub URL
                </label>
                <input
                  type="text"
                  value={formData.socialLinks.github}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    socialLinks: { ...formData.socialLinks, github: e.target.value }
                  })}
                  className="w-full px-3 py-2 bg-[rgba(243,235,226,0.06)] border border-[var(--dusk-line-strong)] rounded-md text-white"
                  placeholder="https://github.com/username"
                />
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="featured"
                checked={formData.featured}
                onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                className="mr-2"
              />
              <label htmlFor="featured" className="text-sm font-medium text-gray-300">
                Featured Member
              </label>
            </div>

            <div className="flex space-x-4">
              <button
                type="submit"
                className="px-6 py-3 bg-gradient-to-r from-teal-700 to-cyan-700 hover:from-teal-600 hover:to-cyan-600 text-white font-semibold rounded-xl transition-all shadow-lg hover:shadow-teal-500/30"
              >
                {editingMember ? 'Update Member' : 'Add Member'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="px-6 py-3 bg-[rgba(243,235,226,0.06)]/50 hover:bg-gray-600/50 text-gray-200 font-semibold rounded-xl transition-all"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {/* Team Members Table */}
        <div className="rounded-2xl border border-[var(--dusk-line)] overflow-hidden bg-[rgba(243,235,226,0.03)] backdrop-blur-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-700/30">
              <thead className="bg-[rgba(243,235,226,0.05)] border-b border-[var(--dusk-line)]">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                    Member
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                    Position
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                    Featured
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/30">
                {members.map((member) => (
                    <tr key={member._id} className="hover:bg-[rgba(243,235,226,0.05)] transition-all">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          {member.imageUrl ? (
                            <img
                              className="h-10 w-10 rounded-full object-cover"
                              src={getFullImageUrl(member.imageUrl)}
                              alt={member.name}
                              onError={handleImageError}
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-gray-600 flex items-center justify-center">
                              <svg className="h-6 w-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                              </svg>
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-white">{member.name}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-300">{member.position}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-300 max-w-xs truncate">{member.description}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {member.featured ? (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          Featured
                        </span>
                      ) : (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                          Regular
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleEdit(member)}
                          className="p-2 text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/20 rounded-lg transition-all" title="Edit"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                        </button>
                        <button
                          onClick={() => handleDelete(member._id)}
                          className="p-2 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded-lg transition-all" title="Delete"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamSection;



